import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { OidcSecurityService } from '../lib/angular-auth-oidc-client-facade';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class OidcAuthService {
  private readonly tokenKey = 'access_token';
  private readonly idTokenKey = 'id_token';
  private readonly refreshTokenKey = 'refresh_token';
  private readonly stateKey = 'oauth_state';
  private readonly nonceKey = 'oauth_nonce';
  private readonly codeVerifierKey = 'code_verifier';

  constructor(
    private router: Router,
    private http: HttpClient,
    private oidcSecurityService: OidcSecurityService,
    private authService: AuthService
  ) {}

  async login(): Promise<void> {
    // Use the OidcSecurityService for authentication
    await this.oidcSecurityService.authorize();
  }

  async handleCallback(code?: string | null, state?: string | null): Promise<boolean> {
    if (!code && !state) {
      const urlParams = new URLSearchParams(window.location.search);
      code = urlParams.get('code');
      state = urlParams.get('state');
    }

    if (!code || !state) {
      console.error('Missing code or state in callback');
      return false;
    }

    // Validate state
    const savedState = sessionStorage.getItem(this.stateKey);
    if (state !== savedState) {
      console.error('State mismatch');
      return false;
    }

    try {
      // Exchange code for tokens
      const tokenResponse = await this.exchangeCodeForTokens(code);

      if (tokenResponse.access_token) {
        this.setTokens(tokenResponse);
        this.cleanupSessionStorage();

        // Notify OidcSecurityService about auth state change
        this.oidcSecurityService.checkAuth();

        return true;
      }
    } catch (error) {
      console.error('Error exchanging code for tokens:', error);
    }

    return false;
  }

  private async exchangeCodeForTokens(code: string): Promise<any> {
    const tokenUrl = `${environment.authority}/connect/token`;
    let codeVerifier = sessionStorage.getItem(this.codeVerifierKey) || sessionStorage.getItem('pkce_code_verifier');

    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      code: code,
      redirect_uri: this.getRedirectUri(),
      client_id: 'crop.service',
      code_verifier: codeVerifier || ''
    });

    try {
      const response = await firstValueFrom(
        this.http.post(tokenUrl, body.toString(), {
          headers: new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded'
          })
        })
      );

      return response;
    } catch (error: any) {
      console.error('Token exchange failed:', error);
      throw error;
    }
  }

  private setTokens(tokenResponse: any): void {
    if (tokenResponse.access_token) {
      this.authService.setToken(tokenResponse.access_token);
    }
    if (tokenResponse.id_token) {
      localStorage.setItem(this.idTokenKey, tokenResponse.id_token);
    }
    if (tokenResponse.refresh_token) {
      localStorage.setItem(this.refreshTokenKey, tokenResponse.refresh_token);
    }

    this.saveTokensInOidcFormat(tokenResponse);
  }

  getAccessToken(): string | null {
    let token = localStorage.getItem(this.tokenKey) || sessionStorage.getItem(this.tokenKey);

    if (!token) {
      const keys = Object.keys(localStorage);
      const authKey = keys.find(key => key.includes('authnResult'));
      if (authKey) {
        try {
          const authData = JSON.parse(localStorage.getItem(authKey) || '{}');
          token = authData.access_token;
        } catch (e) {
          console.error('Error parsing auth data:', e);
        }
      }
    }

    console.log('OidcAuthService - Getting token:', !!token, 'Length:', token?.length);
    return token;
  }

  isAuthenticated(): boolean {
    return this.oidcSecurityService.isAuthenticated();
  }

  logout(): void {
    this.oidcSecurityService.logoff();
  }

  private getRedirectUri(): string {
    // Use window.location.origin like MasofaCropMonitoringWebClient
    return `${window.location.origin}/callback`;
  }

  private generateState(): string {
    return this.generateRandomString(32);
  }

  private generateNonce(): string {
    return this.generateRandomString(32);
  }

  private generateRandomString(length: number): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  private generateCodeVerifier(): string {
    return this.generateRandomString(128);
  }


  private cleanupSessionStorage(): void {
    sessionStorage.removeItem(this.stateKey);
    sessionStorage.removeItem(this.nonceKey);
    sessionStorage.removeItem(this.codeVerifierKey);
   }

   // Save tokens in angular-auth-oidc-client format
   private saveTokensInOidcFormat(tokens: any): void {
    const oidcKey = `oidc.user:${environment.authority}:crop.service`;
    const oidcData = {
      access_token: tokens.access_token,
      token_type: tokens.token_type || 'Bearer',
      profile: tokens.profile || {},
      expires_at: Math.floor(Date.now() / 1000) + (tokens.expires_in || 3600)
    };
    localStorage.setItem(oidcKey, JSON.stringify(oidcData));
  }
}
